import { CardsDataSourceBase } from '../../common/CardsDataSourceBase';
import { injectable } from 'inversify';
import { injectToken } from 'inversify-token';
import { YinzCamAPIResponse } from 'yinzcam-api';
import { YinzCamAppServerToken } from 'yinzcam-appserver';

const HOME_LOGO_PLACEHOLDER = 'https://s3.eu-west-2.amazonaws.com/resources-uk.yinzcam.com/cricket/edg/images/home-team-shield.svg';
const AWAY_LOGO_PLACEHOLDER = 'https://s3.eu-west-2.amazonaws.com/resources-uk.yinzcam.com/cricket/edg/images/away-team-shield.svg';

export type CricketMatchStatus = 'pre' | 'live' | 'post';

const BALL_TEXT_CONTAINER = {
  width: '2rem',
  height: '2rem',
  centerContentHorizontally: true,
  centerContentVertically: true,
} as const;

const DATE_FMT_OPTS: Intl.DateTimeFormatOptions = {
  weekday: 'short',
  day: 'numeric',
  month: 'short',
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
};

// LastBalls arrive newest-first; Ball/@Type="-" marks over separators.
function buildOversJson(lastBalls: any[]): string {
  if (!lastBalls.length) return '';

  const overs: any[][] = [];
  let currentOver: any[] = [];

  for (const ball of lastBalls) {
    const attrs = ball?._attributes;
    if (attrs?.Type === '-') {
      if (currentOver.length) {
        overs.push(currentOver);
        currentOver = [];
      }
    } else {
      currentOver.push(attrs);
    }
  }
  if (currentOver.length) {
    overs.push(currentOver);
  }

  const overEntries = overs.map((balls, i) => {
    const mappedBalls = balls.map((b: any) => ({
      text: {
        text: b?.Text ?? '',
        size: '0.875rem',
        weight: '700',
        container: BALL_TEXT_CONTAINER,
      },
      isScoring: b?.Color !== '#DBDBDE',
    }));

    return {
      balls: mappedBalls,
      txtOverLabel: { text: `OV ${overs.length - i}`, size: '0.75rem', weight: '500' },
    };
  });

  return JSON.stringify(overEntries);
}

@injectable()
export class YinzCamAppServerCricketMatchHeader extends CardsDataSourceBase {
  public constructor(@injectToken(YinzCamAppServerToken) server) {
    super({ server });
  }

  protected processResponse(
    path: string,
    response: YinzCamAPIResponse,
    specData?: { [key: string]: any }
  ): object | any[] {
    const data = response?.data as any;
    const box = data?.Boxscore;
    if (!box) return {};

    const state: string = box?.State?._text ?? '';
    const gameState: string = box?.GameState?._text ?? '';

    const matchStatus: CricketMatchStatus = state === 'P' ? 'pre' : state === 'C' ? 'live' : 'post';

    let matchStateLabel = matchStatus === 'pre' ? 'PRE MATCH' : matchStatus === 'post' ? 'POST MATCH' : 'LIVE';
    if (state === 'C' && gameState && gameState !== 'Live') {
      matchStateLabel = gameState.toUpperCase();
    }

    const homeAttrs = box?.HomeTeam?._attributes ?? {};
    const awayAttrs = box?.AwayTeam?._attributes ?? {};
    // Upstream returns a soccer-themed `_default.svg` placeholder when no team
    // logo is registered; swap in cricket-styled crests so the card looks
    // intentional during the pre-upload window.
    const resolveLogo = (raw: string, fallback: string) => (!raw || raw.includes('_default.svg') ? fallback : raw);
    const homeLogoUrl = resolveLogo(box?.HomeTeam?.ImageUrl?._text ?? '', HOME_LOGO_PLACEHOLDER);
    const awayLogoUrl = resolveLogo(box?.AwayTeam?.ImageUrl?._text ?? '', AWAY_LOGO_PLACEHOLDER);

    const homeScore = box?.Score?.HomeTeamScore?._attributes;
    const awayScore = box?.Score?.AwayTeamScore?._attributes;

    let homeScoreText = homeScore?.ScoreLine ?? '';
    let awayScoreText = awayScore?.ScoreLine ?? '';

    // Post-match: reconstruct score from WormChart inning summaries (ScoreLine
    // is absent in State="F").
    if (state === 'F' && !homeScoreText) {
      const wormInnings = [].concat(box?.WormChart?.Inning ?? data?.WormChart?.Inning).filter(Boolean);
      const inningsFor = (teamId: string) =>
        wormInnings
          .filter((inn: any) => inn?._attributes?.TeamId === teamId)
          .map((inn: any) => inn?._attributes?.Score ?? '')
          .filter(Boolean)
          .join(' & ');
      homeScoreText = inningsFor(homeAttrs.Id);
      awayScoreText = inningsFor(awayAttrs.Id);
    }

    const homeOversText = homeScore?.RunRate ? `(RR ${homeScore.RunRate})` : '';
    const awayOversText = awayScore?.RunRate ? `(RR ${awayScore.RunRate})` : '';

    const lastBalls = [].concat(box?.LastBalls?.Ball).filter(Boolean);
    const oversJson = buildOversJson(lastBalls);

    const venue = box?.Venue?._text ?? '';
    const description = box?.Description?._text ?? '';
    const result = box?.Result?._text ?? '';

    let dateRangeText = '';
    const timestamp = box?.Timestamp?._text;
    if (timestamp) {
      try {
        dateRangeText = new Date(timestamp).toLocaleDateString('en-GB', DATE_FMT_OPTS);
      } catch {
        // ignore parse errors
      }
    }

    return {
      matchStatus,
      txtCompetitionName: { text: description },
      txtDateRange: { text: dateRangeText },
      txtLocationLabel: { text: venue },
      txtMatchStateLabel: { text: matchStateLabel },
      imgTeamA: { source: homeLogoUrl },
      imgTeamB: { source: awayLogoUrl },
      txtHomeTeamName: { text: homeAttrs.Name ?? '' },
      txtAwayTeamName: { text: awayAttrs.Name ?? '' },
      txtHomeTeamScore: { text: homeScoreText },
      txtAwayTeamScore: { text: awayScoreText },
      txtHomeTeamSubscore: { text: homeOversText },
      txtAwayTeamSubscore: { text: awayOversText },
      txtResultLabel: { text: result },
      oversJson,
    };
  }
}
